import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await register(name, email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bw-bg-primary)" }}>
      <Seo
        title="Sign Up Free — BuildWise AI Permit Feasibility for San Diego"
        description="Create a free BuildWise AI account. Get instant San Diego permit feasibility, cost estimates, and timelines for ADUs, decks, remodels, and more. No credit card required."
        path="/signup"
      />
      <Header />
      <div className="max-w-md mx-auto px-6 py-16 md:py-24">
        <div className="label-overline mb-4">Create your account</div>
        <h1 className="font-display font-black text-4xl tracking-tight mb-4" style={{ color: "var(--bw-text-primary)" }}>
          Start building smarter.
        </h1>
        <p className="text-sm mb-10 leading-relaxed" style={{ color: "var(--bw-text-secondary)" }}>
          Free forever for feasibility summaries. Unlock premium reports at $49 or $99, add a 30-day Live AI chat pass for
          $9.99, or hand off the whole permit to our human concierge team starting at $299. Cancel anytime.
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
          <div>
            <label className="label-overline block mb-2">Full name</label>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className="bw-input" data-testid="signup-name" />
          </div>
          <div>
            <label className="label-overline block mb-2">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bw-input" data-testid="signup-email" autoComplete="email" />
          </div>
          <div>
            <label className="label-overline block mb-2">Password (min 6)</label>
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="bw-input" data-testid="signup-password" autoComplete="new-password" />
          </div>
          {error && (
            <div className="text-sm p-3 border" style={{ color: "var(--bw-error)", borderColor: "var(--bw-error)" }} data-testid="signup-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="signup-submit">
            {busy ? "Creating…" : "Create Account"}
          </button>
        </form>

        <div className="mt-10 pt-8 border-t" style={{ borderColor: "var(--bw-border)" }}>
          <div className="label-overline mb-3">What you unlock instantly</div>
          <ul className="text-sm space-y-2" style={{ color: "var(--bw-text-secondary)" }}>
            <li>· AI-guided project interview (~5 minutes)</li>
            <li>· Jurisdiction detection for every San Diego address</li>
            <li>· Free feasibility summary with cost + timeline ranges</li>
            <li>· Access to our verified City Documents Library</li>
          </ul>
        </div>

        <p className="mt-8 text-sm" style={{ color: "var(--bw-text-secondary)" }}>
          Already have an account? <Link to="/login" className="font-semibold underline" data-testid="link-login">Log in</Link>
        </p>
      </div>
    </div>
  );
}
